import { api, enviarFormulario } from '../api';
import type { Edicao, Premissas } from '../cptu/motor';

/**
 * As chamadas da campanha CPTu — onda 312 (spec 07 §3.3 e §4; RN-253).
 *
 * Cada função carrega o caminho INTEIRO, escrito: o caminho é o contrato
 * entre a tela e a API, e o teste 26 procura por ele aqui (o mesmo molde de
 * `api/campo.ts`). Os envios de arquivo (a importação, as fotos e o
 * cabeçalho do CSV) recebem o `FormData` pronto: quem empilha os arquivos é
 * a tela, que conhece o limite de cada rota (o `26.4`).
 *
 * As formas abaixo são as que os controladores devolvem
 * (`api/src/cptu/campanhas.service.ts`, `importacao.service.ts`,
 * `conferencia.ts`). Número que o motor deixou NaN chega `null` (o JSON
 * não tem NaN); número do banco sem `::float8` chega como texto.
 *
 * A onda 313 (a interpretação na tela) acrescenta as leituras da vertical, a
 * dissipação aberta, as escolhas que a tela grava — o PATCH da dissipação e o
 * PUT das camadas — e as premissas da campanha. O resultado do motor nunca vem
 * daqui: a tela o calcula com `cptu/motor` (R2).
 */

// ── A campanha ─────────────────────────────────────────────────────────

/** As situações da campanha (`cptu_campanha.situacao`, 0254). */
export type SituacaoDaCampanha =
  | 'rascunho' | 'em_verificacao' | 'devolvida' | 'aprovada' | 'emitida' | 'cancelada';

/** Só a campanha em rascunho ou devolvida muda (importação, verticais, fotos, cancelamento). */
export const campanhaEditavel = (s: string) => s === 'rascunho' || s === 'devolvida';

/** «CPTU-01» — a sequência da campanha na OS. */
export const codigoDaCampanha = (sequencia: number) => `CPTU-${String(sequencia).padStart(2, '0')}`;

export interface CampanhaDaLista {
  id: string;
  os_id: string;
  os_numero: string;
  cliente: string | null;
  cliente_id: string | null;
  obra: string | null;
  sequencia: number;
  titulo: string;
  situacao: SituacaoDaCampanha;
  criada_em: string;
  verticais: number;
  metragem: number;
  relatorio_numero: string | null;
}

export interface FotoDaVertical {
  id: string;
  vertical_id: string;
  arquivo_id: string;
  /** O nome do arquivo como veio do aparelho. */
  arquivo: string;
  mime: string;
  legenda: string | null;
  no_relatorio: boolean;
  ordem: number;
  tirada_em: string | null;
}

export type MotivoDeParada =
  | 'impenetravel' | 'profundidade_prevista' | 'carga_maxima' | 'desvio_inclinacao' | 'outro';

/** O encerramento por extenso, na ordem da lista. */
export const MOTIVOS_DE_PARADA: [MotivoDeParada, string][] = [
  ['impenetravel', 'Impenetrável ao equipamento'],
  ['profundidade_prevista', 'Profundidade prevista atingida'],
  ['carga_maxima', 'Carga máxima do equipamento'],
  ['desvio_inclinacao', 'Desvio de inclinação'],
  ['outro', 'Outro motivo'],
];

export const textoDoEncerramento = (motivo: string | null, texto: string | null): string | null => {
  if (!motivo) return texto || null;
  const nome = MOTIVOS_DE_PARADA.find(([m]) => m === motivo)?.[1] ?? motivo;
  return motivo === 'outro' && texto ? texto : nome;
};

/** De onde veio a vertical (`formato_origem`). */
export const FORMATO_DE_ORIGEM: Record<string, string> = {
  pagani_xlsx: 'Pagani · vertical',
  pagani_dissipacao_xlsx: 'Pagani · dissipação',
  csv: 'CSV/TXT',
  cpeit_cpt: 'Projeto do CPeT-IT',
};

export interface VerticalDaCampanha {
  id: string;
  campanha_id: string;
  ponto_id: string;
  codigo: string;
  situacao: 'ativa' | 'substituida' | 'cancelada';
  substituida_por: string | null;
  motivo_substituicao: string | null;
  data_ensaio: string | null;
  operador: string | null;
  cone_equipamento_id: string | null;
  cone_codigo: string | null;
  /**
   * Onda 335: sem cone gravado na vertical, o que o número do arquivo acha hoje em Equipamentos (nada se grava;
   * `cone_patrimonio`, `cone_nome` e `cone_numero_serie` são os dele), e se o número casa com mais de um.
   */
  cone_achado_id?: string | null;
  cone_pelo_numero?: boolean;
  cone_numero_ambiguo?: boolean;
  cone_patrimonio: string | null;
  cone_nome: string | null;
  cone_numero_serie: string | null;
  area_cone_cm2: number | null;
  cota: number | null;
  latitude: number | null;
  longitude: number | null;
  /** Onda 334: a mesma coordenada em UTM (SIRGAS 2000), no fuso da própria longitude — calculada no banco. */
  utm_fuso: number | null;
  utm_e: number | null;
  utm_n: number | null;
  /** Onda 338/347: como a coordenada foi lançada (`utm` no fuso `coordenada_fuso`, ou `graus`); null sem registro. */
  coordenada_lancada?: 'utm' | 'graus' | null;
  coordenada_fuso?: number | null;
  nivel_agua: number | null;
  pre_furo_m: number | null;
  profundidade_final: number | null;
  motivo_parada: MotivoDeParada | null;
  motivo_parada_texto: string | null;
  arquivo_bruto_id: string;
  arquivo: string;
  arquivo_hash: string;
  formato_origem: string;
  n_leituras: number;
  observacoes: string | null;
  importada_por_nome: string;
  importada_em: string;
  ponto: { id: string; codigo: string; tipo: string; situacao: string };
  /** A vertical que esta substituiu (R8), a mais recente. */
  substitui: { id: string; importada_em: string; motivo: string | null } | null;
  dissipacoes: number;
  /** As premissas que diferem da campanha; o NA e a cota espelham o cabeçalho (`na`, `cota`). */
  premissas: Partial<Premissas>;
  /** A edição do engenheiro (§5.4), ou null (sem edição). */
  edicao: Edicao | null;
  /** As camadas vigentes (onda 313). */
  n_camadas: number;
  /**
   * A régua «Processada» (onda 313, R16): quando a tela gravou as exceções (as premissas fora do NA e da cota) e a
   * edição pela última vez; null, nunca. A marca das exceções passa na reimportação com as premissas (não quando a
   * vertical nova recebe as do projeto); a da edição não passa. O que a importação grava não marca.
   */
  premissas_da_tela_em: string | null;
  edicao_da_tela_em: string | null;
  fotos: FotoDaVertical[];
}

/** De onde veio a camada: do engenheiro, ou de uma sugestão do motor (`camadas.ts`). */
export type OrigemDaCamada = 'manual' | 'sugestao_tratio' | 'sugestao_agrupamento';

/** Uma camada vigente da vertical (`cptu_camada`, 0255), por topo. */
export interface CamadaDaVertical {
  id: string;
  topo_m: number;
  base_m: number;
  unidade: string;
  descricao: string | null;
  origem: OrigemDaCamada;
  /** Os parâmetros da sugestão que deu origem à camada (janela, limite, grupos…); null na manual. */
  parametros: Record<string, number> | null;
  criada_por_nome: string;
  criada_em: string;
}

/** A vertical aberta na ficha: as fotos retiradas, o resumo das leituras e o histórico do ponto. */
export interface VerticalCompleta extends VerticalDaCampanha {
  fotos_retiradas: FotoDaVertical[];
  resumo: {
    leituras: number;
    profundidade_inicial: string | number | null;
    profundidade_final: string | number | null;
    sem_leitura: number;
    u2_negativo: number;
    inclinacao_maxima: string | number | null;
    dissipacoes: number;
  } | null;
  historico: {
    id: string; situacao: string; importada_em: string; formato_origem: string;
    n_leituras: number; substituida_por: string | null; motivo_substituicao: string | null;
    arquivo: string; importada_por_nome: string;
  }[];
  campanha: {
    id: string; sequencia: number; titulo: string; situacao: SituacaoDaCampanha;
    os_id: string; os_numero: string;
    /** O padrão das premissas da campanha: só o que difere de `PREMISSAS_PADRAO`. */
    premissas: Partial<Premissas>;
  };
  /** As camadas vigentes, por topo (onda 313). */
  camadas: CamadaDaVertical[];
  /** Quantas camadas foram retiradas desta vertical (não se apagam; o histórico as mostra). */
  camadas_retiradas: number;
  /** O que impedia o cone na data do ensaio (`fn_equipamento_impedimento_em`), ou null (apto, ou sem cone). */
  cone_impedimento: string | null;
}

export interface DissipacaoDaCampanha {
  id: string;
  vertical_id: string;
  vertical: string;
  profundidade: number;
  origem: 'pagani_xlsx' | 'cpeit_cpt' | 'csv';
  situacao: string;
  /** null = o u₀ da vertical na profundidade (o `.cpt` grava o do registro do CPeT-IT). */
  u0_kpa: number | null;
  /** G₀, Su e M informados à mão; null = os da própria vertical (a leitura mais próxima). */
  g0_mpa: number | null;
  su_kpa: number | null;
  m_mpa: number | null;
  /** A reta em (√t, u); null = a automática. */
  reta: RetaDaDissipacao | null;
  observacoes: string | null;
  arquivo: string | null;
  pares: number;
  duracao_s: string | number | null;
  /**
   * Alguma escolha da dissipação (u₀, reta, G₀, Su, M, observações) foi gravada depois da importação — pelo
   * PATCH da tela, não pela importação do `.cpt` nem pela reimportação (a régua «Processada», R16).
   */
  escolhas_gravadas: boolean;
  /** A própria reta foi gravada pela tela depois da importação (um UPDATE com `reta`; rodada da 313.5). */
  reta_gravada: boolean;
  /** O próprio u₀ foi gravado pela tela depois da importação (onda 315: a origem «tela» × «projeto» no relatório). */
  u0_gravado?: boolean;
}

/** A reta do trecho inicial da dissipação, em (√t, u). */
export interface RetaDaDissipacao { x1: number; y1: number; x2: number; y2: number }

/** A dissipação aberta: a curva u × t, na ordem do arquivo (t em s, u em kPa). */
export interface DissipacaoCompleta extends DissipacaoDaCampanha {
  tempo: number[];
  u: number[];
}

/**
 * Uma linha de corte da análise espacial (onda 316, R3): o nome («A–B», com a meia-risca), os pontos, e o método e o
 * modelo que a tela tinha ao gravar — o PDF desenha com eles.
 */
export interface LinhaDeCorte {
  nome: string;
  pontos: { latitude: number; longitude: number }[];
  metodo: 'idw' | 'krigagem';
  modelo: 'esferico' | 'exponencial';
}

export interface CampanhaCompleta {
  campanha: {
    id: string; os_id: string; sequencia: number; titulo: string;
    situacao: SituacaoDaCampanha; motivo_situacao: string | null;
    relatorio_id: string | null; relatorio_numero: string | null;
    criada_por_nome: string; criada_em: string;
    /** O padrão das premissas da campanha: só o que difere de `PREMISSAS_PADRAO`. */
    premissas: Partial<Premissas>;
    /** Quando a tela gravou as premissas da campanha (R16, onda 313); null: nunca, ou a importação aplicou as do
     *  projeto por cima. */
    premissas_da_tela_em: string | null;
    /** As linhas de corte da análise espacial (onda 316); `[]` sem nenhuma. */
    linhas_corte: LinhaDeCorte[];
    /** A vista 3D e a imagem do terreno gravadas para o relatório (onda 316, Tarefa 316.5); null sem elas. */
    vista3d_arquivo_id: string | null;
    satelite_arquivo_id: string | null;
  };
  os: { id: string; numero: string; cliente: string | null; cliente_id: string | null; obra: string | null };
  verticais: VerticalDaCampanha[];
  substituidas: number;
  dissipacoes: DissipacaoDaCampanha[];
  pontosDaOs: { id: string; codigo: string; tipo: string; vertical_id: string | null }[];
  motor: { versao: string; homologada: boolean };
}

/** Uma linha do histórico da campanha (a trilha, sem o bruto). */
export interface LinhaDoHistorico {
  id: string;
  tabela: 'cptu_campanha' | 'cptu_vertical' | 'cptu_dissipacao' | 'cptu_foto' | 'cptu_camada';
  registro_id: string;
  vertical: string | null;
  profundidade: number | null;
  operacao: 'INSERT' | 'UPDATE' | 'DELETE';
  datahora: string;
  usuario_nome: string | null;
  perfil: string | null;
  motivo: string | null;
  campos_alterados: string[] | null;
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
  /**
   * As verticais citadas em `vertical_id` e `substituida_por` (antes e depois), pelo identificador — também a
   * passagem de uma camada para a vertical nova, na reimportação.
   */
  verticais?: Record<string, { codigo: string; importada_em: string }>;
  /** Os cones citados em `cone_equipamento_id` (antes e depois), pelo identificador: o patrimônio e o nome. */
  equipamentos?: Record<string, { patrimonio: string; nome: string }>;
  /** Os funcionários citados em `retirada_por` (a camada retirada), pelo identificador: o nome (onda 313). */
  funcionarios?: Record<string, string>;
  /** Na linha de uma camada: o trecho e a unidade dela (a camada não se apaga, então sempre se lê, onda 313). */
  camada?: { topo: number; base: number; unidade: string };
}

export const listarCampanhas = (f: { busca?: string; situacao?: string | null; cliente?: string | null; os?: string }) => {
  const q = new URLSearchParams();
  if (f.busca?.trim()) q.set('busca', f.busca.trim());
  if (f.situacao) q.set('situacao', f.situacao);
  if (f.cliente) q.set('cliente', f.cliente);
  if (f.os) q.set('os', f.os);
  const s = q.toString();
  return api<CampanhaDaLista[]>(`/cptu/campanhas${s ? `?${s}` : ''}`);
};

export const criarCampanha = (osId: string, titulo: string) =>
  api<CampanhaCompleta>('/cptu/campanhas', { metodo: 'POST', corpo: { osId, titulo } });

export const verCampanha = (id: string) => api<CampanhaCompleta>(`/cptu/campanhas/${id}`);

export const cancelarCampanha = (id: string, motivo: string) =>
  api<CampanhaCompleta>(`/cptu/campanhas/${id}/cancelar`, { metodo: 'POST', corpo: { motivo } });

export const historicoDaCampanha = (id: string) =>
  api<LinhaDoHistorico[]>(`/cptu/campanhas/${id}/historico`);

/**
 * O título e as premissas da campanha, com motivo. As premissas vão SEMPRE inteiras (R10): o PATCH troca o
 * objeto todo, e a tela parte do gravado — `{ ...campanha.premissas, ...mudancas }`; a API guarda só o que
 * difere do padrão.
 */
export const alterarCampanha = (id: string, corpo: { premissas?: Partial<Premissas>; titulo?: string; motivo: string }) =>
  api<CampanhaCompleta>(`/cptu/campanhas/${id}`, { metodo: 'PATCH', corpo });

/**
 * As linhas de corte da análise espacial, como conjunto (onda 316, R3): até 6, com motivo; `[]` tira todas. Só na
 * campanha em rascunho ou devolvida. Devolve a campanha.
 */
export const gravarLinhasDeCorte = (id: string, corpo: { linhas: LinhaDeCorte[]; motivo: string }) =>
  api<CampanhaCompleta>(`/cptu/campanhas/${id}/linhas-de-corte`, { metodo: 'PUT', corpo });

// ── O cone, no parque ─────────────────────────────────────────────────

export interface EquipamentoDoParque {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  numero_serie: string | null;
  status: string;
}

/** Procurar o cone em Equipamentos (pelo código, nome, fabricante, modelo ou número de série). */
export const procurarNoParque = (busca: string) =>
  api<EquipamentoDoParque[]>(`/equipamentos?busca=${encodeURIComponent(busca)}`);

// ── A vertical e as fotos ─────────────────────────────────────────────

export const verVertical = (id: string) => api<VerticalCompleta>(`/cptu/verticais/${id}`);

export interface AlteracaoDaVertical {
  nivelAgua?: number | null;
  cota?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Onda 334: a coordenada em UTM (SIRGAS 2000, sul) — E, N e fuso juntos, no lugar da latitude e longitude. */
  utmE?: number;
  utmN?: number;
  utmFuso?: number;
  coneEquipamentoId?: string | null;
  motivoParada?: MotivoDeParada | null;
  motivoParadaTexto?: string | null;
  observacoes?: string | null;
  /**
   * As premissas da vertical, INTEIRAS (R10): o PATCH troca o objeto todo, e a tela parte do gravado sem o NA e a
   * cota (`{ ...(vertical.premissas sem na e cota), ...mudancas }`) — o NA e a cota vão por `nivelAgua` e `cota`.
   */
  premissas?: Partial<Premissas>;
  /** A edição inteira (a tela parte de `structuredClone(EDICAO_VAZIA)`); null tira a edição. */
  edicao?: Edicao | null;
  motivo: string;
}

export const alterarVertical = (id: string, corpo: AlteracaoDaVertical) =>
  api<VerticalCompleta>(`/cptu/verticais/${id}`, { metodo: 'PATCH', corpo });

/** Uma vertical no lote da Edição rápida (onda 333): o id e só o que mudou no cabeçalho. */
export type VerticalDoLote = { id: string } & Pick<AlteracaoDaVertical,
  'nivelAgua' | 'cota' | 'latitude' | 'longitude' | 'utmE' | 'utmN' | 'utmFuso' | 'coneEquipamentoId' | 'motivoParada'
  | 'motivoParadaTexto'>;

/**
 * A Edição rápida das verticais (onda 333): o cabeçalho de várias verticais com um motivo, numa transação — todas ou
 * nenhuma. O erro de uma volta no caminho `verticais.<i>` (a posição no lote). Devolve a campanha relida.
 */
export const alterarVerticais = (campanhaId: string, verticais: VerticalDoLote[], motivo: string) =>
  api<CampanhaCompleta>(`/cptu/campanhas/${campanhaId}/verticais`, { metodo: 'PATCH', corpo: { verticais, motivo } });

/** As leituras do equipamento, colunares, na ordem do arquivo; null = sem leitura (o motor verá NaN). */
export interface LeiturasDaVertical {
  ordem: number[];
  profundidade: (number | null)[];
  qc: (number | null)[];
  fs: (number | null)[];
  u2: (number | null)[];
  inclinacao: (number | null)[];
  velocidade: (number | null)[];
  tempo: (number | null)[];
}

export const leiturasDaVertical = (id: string) => api<LeiturasDaVertical>(`/cptu/verticais/${id}/leituras`);

/** As camadas como a tela as grava: o conjunto inteiro, na ordem do topo, em metros com duas casas. */
export interface GravacaoDasCamadas {
  /** Até 200; a lista vazia retira todas. */
  camadas: {
    topo: number;
    base: number;
    unidade: string;
    descricao?: string | null;
    origem: OrigemDaCamada;
    parametros?: Record<string, number> | null;
  }[];
  motivo: string;
}

/**
 * As camadas da vertical, como conjunto (onda 313, R12): as vigentes iguais ficam, as que não vieram são
 * retiradas (nada se apaga), as novas entram. Devolve a vertical, com `camadas`.
 */
export const gravarCamadas = (id: string, corpo: GravacaoDasCamadas) =>
  api<VerticalCompleta>(`/cptu/verticais/${id}/camadas`, { metodo: 'PUT', corpo });

/**
 * Os tetos da gravação das camadas — os MESMOS números da API (`api/src/cptu/premissas-validas.ts`: `LIMITE_DE_CAMADAS` e
 * `LIMITE_DE_VERTICAIS_NO_LOTE`; o 333.77 confere que são iguais). O lote é tudo ou nada: a tela bloqueia antes de mandar.
 */
// ESCOLHA DA TELA, SEM FONTE [cam.teto-do-lote] (na lista `ESCOLHAS_DO_3D`)
export const LIMITE_DE_CAMADAS = 200;
export const LIMITE_DE_VERTICAIS_NO_LOTE = 100;

/** As camadas de várias verticais da campanha num lote: um motivo e, por vertical, o conjunto dela (onda 347). */
export interface GravacaoDasCamadasDaCampanha {
  motivo: string;
  /** Até 100 verticais da campanha (`LIMITE_DE_VERTICAIS_NO_LOTE`), sem repetir; cada `camadas` como em `GravacaoDasCamadas`. */
  verticais: { verticalId: string; camadas: GravacaoDasCamadas['camadas'] }[];
}

/** Quantas camadas ficaram vigentes em cada vertical do lote. */
export interface CamadasDaCampanhaGravadas {
  verticais: { verticalId: string; vigentes: number }[];
}

/**
 * As camadas de várias verticais da campanha de uma vez (onda 347, «Gravar as camadas de todas as verticais»):
 * numa transação só — todas ou nenhuma. A recusa de uma vertical volta com o caminho `verticais.<i>.…` e o
 * código dela na frase.
 */
export const gravarCamadasDaCampanha = (campanhaId: string, corpo: GravacaoDasCamadasDaCampanha) =>
  api<CamadasDaCampanhaGravadas>(`/cptu/campanhas/${campanhaId}/camadas`, { metodo: 'PUT', corpo });

// ── A dissipação ──────────────────────────────────────────────────────

export const verDissipacao = (id: string) => api<DissipacaoCompleta>(`/cptu/dissipacoes/${id}`);

/** As escolhas da dissipação (onda 313, R11): só o que vem muda; null volta ao padrão. */
export interface AlteracaoDaDissipacao {
  /** Finito, 0 ou mais; null = o u₀ da vertical na profundidade. */
  u0Kpa?: number | null;
  /** Finitos, x1 ≠ x2; null = a automática. */
  reta?: RetaDaDissipacao | null;
  /** Finitos e maiores que zero; null = os da própria vertical. */
  g0Mpa?: number | null;
  suKpa?: number | null;
  mMpa?: number | null;
  /** Até 4.000 caracteres. */
  observacoes?: string | null;
  /** 5 caracteres ou mais. */
  motivo: string;
}

export const alterarDissipacao = (id: string, corpo: AlteracaoDaDissipacao) =>
  api<DissipacaoCompleta>(`/cptu/dissipacoes/${id}`, { metodo: 'PATCH', corpo });

/** O envio de um lote de fotos (até `LIMITE_FOTOS` = 8 por pedido; quem parte em lotes é a tela). */
export const enviarFotosDaVertical = (id: string, formulario: FormData, aoProgredir?: (f: number) => void) =>
  enviarFormulario<VerticalCompleta>(`/cptu/verticais/${id}/fotos`, formulario, aoProgredir);

export const alterarFoto = (id: string, corpo: {
  legenda?: string | null; noRelatorio?: boolean; ativa?: boolean; motivo?: string;
}) => api<VerticalCompleta>(`/cptu/fotos/${id}`, { metodo: 'PATCH', corpo });

// ── A importação ──────────────────────────────────────────────────────

export type TipoDeAviso = 'atencao' | 'info';
export interface AvisoDaConferencia { tipo: TipoDeAviso; texto: string }

export interface DataLida {
  iso: string | null;
  alternativa: string | null;
  origem: 'texto_mes_dia' | 'serial_trocado' | 'cpt' | 'csv' | 'nenhuma';
  aviso: string | null;
}

export type FormatoCptu = 'pagani_xlsx' | 'pagani_dissipacao_xlsx' | 'csv' | 'cpeit_cpt';

export interface ConferenciaDaVertical {
  arquivo: string;
  formato: FormatoCptu;
  /** O código do ponto da OS (o da OS ganha: «CPTU01» na OS × «CPTu01» no arquivo). */
  codigo: string;
  /** O código como o arquivo o trouxe — no projeto do CPeT-IT, é por ele que a decisão acha a vertical. */
  codigoLido: string;
  ponto: { id: string | null; codigo: string; tipo: string | null; acao: 'usar' | 'criar' | 'recusar'; motivo?: string };
  substitui: { verticalId: string; importadaEm: string } | null;
  data: DataLida;
  leituras: number;
  profundidadeInicial: number | null;
  profundidadeFinal: number | null;
  passoCm: number | null;
  cone: { codigo: string | null; equipamentoId: string | null; impedimento: string | null };
  na: number | null;
  cota: number | null;
  coordenada: { latitude: number; longitude: number } | null;
  /** A data que se grava se nada mudar: a decidida, a lida, ou a da importação anterior. */
  dataEnsaio: string | null;
  operador: string | null;
  preFuroM: number | null;
  encerramento: { motivo: MotivoDeParada | null; texto: string | null };
  observacoes: string | null;
  /**
   * O que veio da vertical substituída, porque o arquivo não traz e a decisão não mudou (revisão final
   * da 312): a tela diz «da importação anterior».
   */
  daAnterior: CampoDaAnterior[];
  inclinacaoMaxima: { graus: number; profundidade: number } | null;
  velocidade: { media: number | null; foraDaFaixa: { de: number; ate: number }[] };
  u2Negativo: { de: number; ate: number; minimo: number }[];
  /**
   * O arquivo de antes, nesta importação, com os mesmos bytes: esta é a cópia que chega depois — a tela a traz
   * desmarcada, e gravar as duas juntas é 409 (agent-browser da 312).
   */
  copiaDe: string | null;
  avisos: AvisoDaConferencia[];
}

/** Os campos que a reimportação traz da vertical substituída quando o arquivo novo não traz. */
export type CampoDaAnterior =
  | 'data' | 'operador' | 'cone' | 'na' | 'cota' | 'coordenada' | 'preFuro' | 'encerramento' | 'observacoes'
  | 'premissas';

export interface ConferenciaDaDissipacao {
  arquivo: string;
  vertical: string | null;
  profundidade: number;
  pares: number;
  tMax: number;
  avisos: AvisoDaConferencia[];
}

export interface Conferencia {
  simulado: boolean;
  campanhaId: string;
  verticais: ConferenciaDaVertical[];
  dissipacoes: ConferenciaDaDissipacao[];
  recusados: { arquivo: string; motivo: string }[];
  premissasDoProjeto: Record<string, unknown> | null;
  podeGravar: boolean;
  gravado?: {
    verticais: { codigo: string; id: string }[];
    leituras: number;
    dissipacoes: number;
    mantidas: { vertical: string; profundidade: number; arquivo: string }[];
    pares: number;
    /** As verticais que substituíram a anterior do mesmo ponto, e quantas fotos (na vertical) passaram para elas. */
    substituidas: { codigo: string; fotos: number }[];
  };
}

export interface DecisaoDaVertical {
  arquivo: string;
  codigo: string;
  dataEnsaio: string;
  coneEquipamentoId?: string | null;
  nivelAgua?: number | null;
  cota?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  preFuroM?: number | null;
  operador?: string | null;
  /** `null` apaga o encerramento que veio da importação anterior. */
  motivoParada?: MotivoDeParada | null;
  motivoParadaTexto?: string | null;
  observacoes?: string | null;
  motivoSubstituicao?: string;
}

export type ColunaDoMapa = number | string;
export interface MapaDeColunas {
  delimitador: ';' | ',' | 'tab' | '|';
  decimal: ',' | '.';
  linhaCabecalho: number;
  primeiraLinhaDados: number;
  colunas: {
    profundidade: { coluna: ColunaDoMapa; unidade: 'm' | 'cm' };
    qc: { coluna: ColunaDoMapa; unidade: 'MPa' | 'kPa' };
    fs: { coluna: ColunaDoMapa; unidade: 'kPa' | 'MPa' };
    u2: { coluna: ColunaDoMapa; unidade: 'kPa' | 'MPa' };
    inclinacao?: { coluna: ColunaDoMapa };
    velocidade?: { coluna: ColunaDoMapa; unidade: 'cm/s' | 'mm/s' };
    tempo?: { coluna: ColunaDoMapa };
  };
}

export interface Decisoes {
  verticais: DecisaoDaVertical[];
  dissipacoes: { arquivo: string; profundidade: number; vertical: string }[];
  csv?: { modeloId?: string; mapa?: MapaDeColunas };
  aplicarPremissasDoProjeto?: boolean;
}

export interface ModeloDeImportacao {
  id: string;
  nome: string;
  delimitador: MapaDeColunas['delimitador'];
  decimal: MapaDeColunas['decimal'];
  linha_cabecalho: number;
  primeira_linha_dados: number;
  colunas: MapaDeColunas['colunas'];
  ativo: boolean;
  criado_por_nome: string;
  verticais: number;
}

export interface CabecalhoDoCsv {
  arquivo: string;
  delimitador: MapaDeColunas['delimitador'];
  decimal: MapaDeColunas['decimal'];
  colunas: string[];
  amostra: string[][];
  linhaCabecalho: number;
  primeiraLinhaDados: number;
  /** Os modelos ativos que servem para o arquivo: `titulo` casou pelos títulos; `posicao` só pela posição (fraco). */
  modelos: (ModeloDeImportacao & { por: 'titulo' | 'posicao' })[];
}

/**
 * Conferir (`simular`) ou gravar os arquivos de uma campanha — um pedido
 * só, com até 30 arquivos: a dissipação se liga à vertical no mesmo envio.
 */
export const importarArquivosCptu = (id: string, formulario: FormData, aoProgredir?: (f: number) => void) =>
  enviarFormulario<Conferencia>(`/cptu/campanhas/${id}/importar`, formulario, aoProgredir);

/** O cabeçalho de um CSV/TXT e os modelos que servem para ele (nada se grava). */
export const lerCabecalhoDoCsv = (formulario: FormData) =>
  enviarFormulario<CabecalhoDoCsv>('/cptu/csv/cabecalho', formulario);

export const listarModelosDeImportacao = () =>
  api<ModeloDeImportacao[]>('/cptu/modelos-importacao');

export const criarModeloDeImportacao = (corpo: { nome: string } & MapaDeColunas) =>
  api<ModeloDeImportacao>('/cptu/modelos-importacao', { metodo: 'POST', corpo });

/** O mapa de um modelo salvo, na forma da decisão. */
export const mapaDoModelo = (m: ModeloDeImportacao): MapaDeColunas => ({
  delimitador: m.delimitador, decimal: m.decimal,
  linhaCabecalho: m.linha_cabecalho, primeiraLinhaDados: m.primeira_linha_dados,
  colunas: m.colunas,
});

// ── O relatório da campanha (onda 315, Tarefa 315.3) ──────────────────

/** As seções do texto do relatório (R7): a 4 vem do cadastro de Equipamentos e não se edita. */
export type SecaoDoTexto = '1' | '2' | '3' | '4' | '5' | '6' | '7';

/** Os ábacos que o PDF desenha (o de Schneider não: sem gabarito, RN-252). */
export type AbacoDoPdf = 'sbtn' | 'sbt' | 'sbtnMod' | 'qtBq';

/** O que entra no PDF (`cptu_campanha.paginas`, R7). */
export interface PaginasDoRelatorio {
  quadroResumo: boolean; croqui: boolean; perfisIntegrados: boolean; analiseIntegrada: boolean; boletim: boolean;
  /** Dentro da Análise integrada (onda 316, 316.6): as folhas «Seção A–B» (uma por linha gravada) e a «Vista 3D». */
  analiseSecoes: boolean; analiseVista3d: boolean;
  abacos: AbacoDoPdf[]; parametros: string[]; camadas: boolean; dissipacoes: boolean; fotos: boolean;
  /** Os anexos do sistema, cada um opcional (onda 337): as premissas (A), as equações (B), a conformidade (C). */
  premissas: boolean; equacoes: boolean; conformidade: boolean;
  /** As folhas das verticais em pé, enquadradas (onda 337; padrão ligado). */
  verticaisEmPe: boolean;
  /** O qt pintado pela zona SBT e o Qtn pela SBTn (pedido do Humberto, 12/10/2026); padrão ligado. */
  trilhasColoridasPorZona: boolean;
  /**
   * O fundo da prancha de locação (onda 338, RN-267): branco ou o satélite capturado do mapa. Sem escolha, o satélite
   * quando há croqui capturado do mapa, senão o branco.
   */
  pranchaFundo?: 'branco' | 'satelite';
  /** O giro da prancha de locação, em graus no sentido horário (o `bearing` do mapa); 0 = norte para cima. */
  pranchaGiro: number;
}

/** Um assinante do relatório: nome, cargo e o instante do fato (ISO). */
export interface AssinanteDoRelatorio { nome: string; cargo: string | null; em: string }

/** O relatório da campanha (o `relatorio` do fluxo, R1). */
export interface RelatorioDaCampanha {
  id: string; numero: string; revisao: number; status: string; token: string;
  motivoRevisao: string | null; motivoDevolucao: string | null;
  elaborado: AssinanteDoRelatorio | null; verificado: AssinanteDoRelatorio | null;
  aprovado: AssinanteDoRelatorio | null; emitido: AssinanteDoRelatorio | null;
  disponibilizadoEm: string | null; substituidoEm: string | null;
  pdfArquivoId: string | null; planilhaArquivoId: string | null;
}

/** Uma folha da composição do PDF (`composicaoDoRelatorio`). */
export interface FolhaComposta {
  tipo: 'texto' | 'resumo' | 'croqui' | 'analise' | 'boletim' | 'abacos' | 'parametros' | 'camadas' | 'dissipacoes'
    | 'fotos' | 'anexos';
  orientacao: 'retrato' | 'paisagem';
  vertical: string | null;
  titulo: string;
  trilhas: string[];
  abacos: string[];
  colunas: string[];
  fora: { coluna: string; motivo: string }[];
}

/** Um documento anexo ao relatório (o «Anexo D» do cliente, por exemplo). */
export interface AnexoDoRelatorio {
  arquivoId: string; titulo: string; origem: 'cliente' | 'testesolo'; paginas: number | null;
}

/** As ações do relatório (o `pode` e os `motivos` do `RelatorioCompleto`). */
export type AcaoDoRelatorio =
  | 'editar' | 'previa' | 'planilha' | 'enviar' | 'verificar' | 'devolver' | 'emitir' | 'revisao' | 'disponibilizar';

/** A aba Relatório (`GET /cptu/campanhas/:id/relatorio`). */
export interface RelatorioCompleto {
  secoes: { chave: SecaoDoTexto; titulo: string; origem: 'padrao' | 'editado' | 'cadastro'; texto: string; resolvido: string }[];
  campos: string[];
  paginas: PaginasDoRelatorio;
  paginasPadrao: PaginasDoRelatorio;
  parametrosPossiveis: { grandeza: string; rotulo: string; homologada: boolean; motivo: string | null }[];
  analiseIntegradaDisponivel: boolean;
  composicao: FolhaComposta[];
  /** Por vertical, o que as premissas tiram da homologação (R5), com o porquê — o anexo C lista (Tarefa 315.5). */
  fora?: Record<string, { coluna: string; motivo: string }[]>;
  /** As partes e a folha em que cada uma começa (null sem o Chromium no servidor). */
  sumario: { titulo: string; folha: number }[] | null;
  paginasDoDocumento: number | null;
  croqui: {
    origem: 'mapa' | 'imagem'; metrosPorPixel: number | null; largura: number; altura: number;
    enquadramento: [[number, number], [number, number]] | null; zoom: number | null; fundo: string | null;
    credito: string | null; arquivoId: string | null;
  } | null;
  verticaisSemCoordenada: string[];
  fotos: { comFoto: number; verticais: { codigo: string; fotos: number }[]; heic: number };
  anexos: AnexoDoRelatorio[];
  falta: { codigo: string; texto: string }[];
  /** O que quem pergunta pode fazer agora, na situação de agora (Tarefa 315.4). */
  pode: Record<AcaoDoRelatorio, boolean>;
  /** Por que cada ação bloqueada está bloqueada (null na que se pode) — a tela a mostra bloqueada com o motivo. */
  motivos: Record<AcaoDoRelatorio, string | null>;
  relatorio: RelatorioDaCampanha | null;
  /** A corrente e as anteriores (R00, R01…), da mais nova para a mais velha. */
  revisoes: RelatorioDaCampanha[];
  eu: { podeVerificar: boolean; podeAprovar: boolean; motivo: string | null };
  situacao: SituacaoDaCampanha;
  motor: { versao: string; homologada: boolean };
}

export const verRelatorio = (id: string) => api<RelatorioCompleto>(`/cptu/campanhas/${id}/relatorio`);

/** A prévia preliminar do PDF (R3) — para o `LinkProtegido`. */
export const caminhoDaPrevia = (id: string) => `/cptu/campanhas/${id}/relatorio.pdf`;

/** A montagem do relatório (sem motivo): os textos (null volta ao padrão), as páginas, o croqui desenhado e os anexos. */
export interface MontagemDoRelatorio {
  textos?: Partial<Record<SecaoDoTexto, string | null>>;
  paginas?: Partial<PaginasDoRelatorio>;
  croqui?: null;
  anexos?: { arquivoId: string; titulo: string; origem: 'cliente' | 'testesolo' }[];
}

export const montarRelatorio = (id: string, corpo: MontagemDoRelatorio) =>
  api<CampanhaCompleta>(`/cptu/campanhas/${id}`, { metodo: 'PATCH', corpo });

/** O croqui: a imagem (`imagem`) e a moldura (`meta`, JSON) — capturado do Mapa Geotécnico ou enviado. */
export const enviarCroqui = (id: string, formulario: FormData, aoProgredir?: (f: number) => void) =>
  enviarFormulario<CampanhaCompleta>(`/cptu/campanhas/${id}/croqui`, formulario, aoProgredir);

/** A vista 3D para o relatório (onda 316, 316.6): a imagem capturada na aba Análise integrada (`imagem`, até 12 MB). */
export const enviarVista3d = (id: string, formulario: FormData, aoProgredir?: (f: number) => void) =>
  enviarFormulario<CampanhaCompleta>(`/cptu/campanhas/${id}/vista3d`, formulario, aoProgredir);

/** Tira a vista 3D do relatório (o arquivo fica no acervo). */
export const tirarVista3d = (id: string) => api<CampanhaCompleta>(`/cptu/campanhas/${id}/vista3d`, { metodo: 'DELETE' });

/** Um documento anexo ao relatório: o PDF (`pdf`, até 20 MB) e o título e a origem (`meta`, JSON). */
export const enviarAnexo = (id: string, formulario: FormData, aoProgredir?: (f: number) => void) =>
  enviarFormulario<CampanhaCompleta>(`/cptu/campanhas/${id}/anexos`, formulario, aoProgredir);

// ── O fluxo e a emissão (Tarefa 315.4) ───────────────────────────────

/** Envia para a verificação (R1): cria a R00, continua a devolvida, ou cria a revisão depois de «Abrir revisão». */
export const enviarParaVerificacao = (id: string) =>
  api<RelatorioCompleto>(`/cptu/campanhas/${id}/enviar`, { metodo: 'POST' });

/** Verifica o relatório (quem elaborou recebe a frase do §15). */
export const verificarCampanha = (id: string) =>
  api<RelatorioCompleto>(`/cptu/campanhas/${id}/verificar`, { metodo: 'POST' });

/** Devolve para correção, com o motivo (5 caracteres ou mais). */
export const devolverCampanha = (id: string, motivo: string) =>
  api<RelatorioCompleto>(`/cptu/campanhas/${id}/devolver`, { metodo: 'POST', corpo: { motivo } });

/** O que «Aprovar e emitir» devolve: o relatório emitido, o hash do PDF e o nome do arquivo. */
export interface EmissaoDaCampanha { relatorioId: string; numero: string; revisao: number; hash: string; arquivo: string }

/** Aprova e emite (R4) — o RT; na campanha `aprovada` (a emissão que caiu no meio), refaz só a emissão. */
export const emitirCampanha = (id: string) =>
  api<EmissaoDaCampanha>(`/cptu/campanhas/${id}/emitir`, { metodo: 'POST' });

/** Abre a revisão da campanha emitida (R8), com o motivo (5 caracteres ou mais). */
export const abrirRevisao = (id: string, motivo: string) =>
  api<RelatorioCompleto>(`/cptu/campanhas/${id}/revisao`, { metodo: 'POST', corpo: { motivo } });

/** Disponibiliza o relatório emitido no Portal (a rota do Portal que já existe). */
export const disponibilizarRelatorio = (relatorioId: string) =>
  api<unknown>(`/portal/relatorio/${relatorioId}/disponibilizar`, { metodo: 'POST' });

/** A planilha do estado de agora, PRELIMINAR — para o `LinkProtegido`. */
export const caminhoDaPlanilhaPrevia = (id: string) => `/cptu/campanhas/${id}/planilha.xlsx`;

/** O PDF emitido de uma revisão (os bytes do acervo) — para o `LinkProtegido`. */
export const caminhoDoPdfEmitido = (relatorioId: string) => `/cptu/relatorios/${relatorioId}/pdf`;

/** A planilha emitida de uma revisão (os bytes do acervo) — para o `LinkProtegido`. */
export const caminhoDaPlanilhaEmitida = (relatorioId: string) => `/cptu/relatorios/${relatorioId}/planilha.xlsx`;

/** A vertical no pacote da última emissão: o «antes» do «Comparar com o emitido» (R9). Os números vão inteiros (null = NaN). */
export interface EmitidoDaVertical {
  numero: string; revisao: number; emitidoEm: string; motor: string;
  premissas: Premissas; edicao: Edicao | null;
  leituras: { profundidade: (number | null)[]; qc: (number | null)[]; fs: (number | null)[]; u2: (number | null)[] };
}

export const emitidoDaVertical = (campanhaId: string, codigo: string) =>
  api<EmitidoDaVertical>(`/cptu/campanhas/${campanhaId}/emitido/${encodeURIComponent(codigo)}`);
